import React, { useMemo, useState } from "react"
import { t } from "i18next"
import { Button, Card, CardBody } from "reactstrap"
import UploadDocumentModal from "./UploadDocumentModal"
import useDisclosure from "hooks/useDisclosure"
import useParamsQuery from "hooks/useParamsQuery"
import useInitialData from "hooks/useInitialData"
import { useMutation, useQuery } from "@tanstack/react-query"
import SearchInput from "components/Common/SearchInput"
import Paginate from "components/Common/Paginate"
import { extractMultiDocument, getDocuments } from "api/docApi"
import DataTable from "components/Common/DataTable"
import moment from "moment"
import StatusBadge from "components/Common/StatusBadge"
import DocumentRowAction from "./DocumentRowAction"
import lodash from "lodash"
import LoadingButton from "components/Common/LoadingButton"
import { toast } from "react-toastify"
import { Link } from "react-router-dom"
import Select from "react-select"
import { status_options } from "constants/select-options"
import useFetchOptions from "hooks/useFetchOptions"

function DocumentsTable() {
  const [rowSelection, setRowSelection] = useState({})
  const uploadModal = useDisclosure()
  const { params, addParam, deleteParam } = useParamsQuery()

  const { data, isLoading, error } = useQuery({
    queryKey: ["documents", params],
    queryFn: () => getDocuments(params),
  })
  const { results, count } = useInitialData(data)

  const extractMutation = useMutation({
    mutationFn: () => {
      return extractMultiDocument({
        document: lodash.keys(rowSelection),
      })
    },
    onSuccess: () => setRowSelection({}),
    onError: error => toast.error(error),
  })

  const onSearch = value => {
    value ? addParam({ filename: value }) : deleteParam("filename")
  }

  const handleQueryParams = async (name, value) => {
    value ? addParam({ [name]: value }) : deleteParam(name)
  }

  const folderSelect = useFetchOptions({
    url: "/documentations/folder/select/",
    queryKey: "folder_select",
  })

  const columns = useMemo(
    () => [
      {
        accessorKey: "filename",
        header: "name",
        cell: ({ row }) => {
          const { original } = row
          return (
            <Link
              to={{
                pathname: "/analysis",
                search: `upload_file=${original.filename}`,
              }}
            >
              {lodash.truncate(original.filename)}
            </Link>
          )
        },
      },
      {
        accessorKey: "folder",
        header: "folder",
        cell: ({ row }) => {
          return (
            <div className="hstack gap-2">
              {row.original.folder.map(item => (
                <StatusBadge
                  color="secondary"
                  label={item.name}
                  key={item.id}
                />
              ))}
            </div>
          )
        },
      },
      {
        accessorKey: "status",
        header: "status",
        cell: ({ row }) => {
          return <StatusBadge label={row.original.status} />
        },
      },
      {
        accessorKey: "added_date",
        header: "added date",
        cell: ({ row }) => {
          return moment(row.original.created_at).format("l LT")
        },
      },
      {
        accessorKey: "action",
        header: "action",
        cell: ({ row }) => {
          return <DocumentRowAction row={row} />
        },
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <UploadDocumentModal
        isOpen={uploadModal.isOpen}
        toggle={uploadModal.toggle}
      />
      <Card>
        <CardBody>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="hstack gap-2 flex-wrap">
              <SearchInput onSearch={onSearch} defaultValue={params.name} />
              <Select
                className="w-2xl"
                defaultValue={
                  params.folder && {
                    label: params.folder,
                    value: params.folder,
                  }
                }
                options={folderSelect.options}
                isLoading={folderSelect.isLoading}
                onMenuOpen={folderSelect.fetchOptions}
                onChange={option => handleQueryParams("folder", option?.label)}
                placeholder={("filter by folder")}
                isClearable
              />
              <Select
                className="w-2xl"
                defaultValue={
                  params.status && {
                    label: params.status,
                    value: params.status,
                  }
                }
                options={status_options}
                onChange={option => handleQueryParams("status", option?.value)}
                placeholder={("filter by status")}
                isClearable
              />
            </div>
            <div className="hstack gap-2">
              {!lodash.isEmpty(rowSelection) ? (
                <LoadingButton
                  color="secondary"
                  isLoading={extractMutation.isPending}
                  onClick={extractMutation.mutate}
                >
                  {t("extract")}
                </LoadingButton>
              ) : null}
              <Button color="primary" onClick={uploadModal.open}>
                {t("import documents")}
              </Button>
            </div>
          </div>
          <DataTable
            data={results}
            isLoading={isLoading}
            error={error}
            columns={columns}
            rowSelection={rowSelection}
            onSelectionChange={setRowSelection}
          />
          <div className="d-flex align-items-center justify-content-end">
            <Paginate
              count={count}
              size={params.size || 10}
              forcePage={+params.page || 1}
              onPageChange={page => addParam({ page: page.selected + 1 })}
            />
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  )
}

export default DocumentsTable
